import React, { useState, useEffect, createContext, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import PageLoading from './components/PageLoading';
import Footer from './components/Footer';
import sseManager from './services/sseManager';
import { API_URL } from './config';
import './App.css';
import './styles/base/modals.css';
import './styles/components/idea-editor.css';
import './styles/components/page-loading.css';

// 使用 React.lazy 进行代码分割 - 懒加载页面组件
const HomePage = lazy(() => import('./components/HomePage'));
const ArticleList = lazy(() => import('./components/ArticleList'));
const ArticleDetail = lazy(() => import('./components/ArticleDetail'));
const ArticleEditor = lazy(() => import('./components/ArticleEditor'));
const WeeklyList = lazy(() => import('./components/WeeklyList'));
const WeeklyDetail = lazy(() => import('./components/WeeklyDetail'));
const IdeasPage = lazy(() => import('./components/IdeasPage'));
const CreateIdea = lazy(() => import('./components/CreateIdeaModal'));
const Register = lazy(() => import('./components/Register'));
const StashTypeList = lazy(() => import('./components/StashTypeList'));
const StashList = lazy(() => import('./components/StashList'));
const StashDetail = lazy(() => import('./components/StashDetail'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));

// 全局错误边界组件
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // 检查是否是已知的markdown错误
    const isKnownMarkdownError = error.message && (
      error.message.includes('this.getData is not a function') ||
      error.message.includes('exitCodeText') ||
      error.message.includes('Markdown') ||
      error.stack?.includes('index.js')
    );

    if (!isKnownMarkdownError) {
      // 只对未知错误记录详细信息
      console.error('全局错误边界捕获到未知错误:', error, errorInfo);
    } else {
      // 对于已知错误，仅在开发模式下记录
      if (process.env.NODE_ENV === 'development') {
        console.log('🛡️ 全局错误边界捕获到已知markdown错误，已静默处理');
      }
    }

    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // 静默渲染应用，不显示错误信息
      return this.props.children;
    }

    return this.props.children;
  }
}

// 立即禁用 React DevTools
window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = { isDisabled: true };

// 强化全局错误拦截器 - 捕获所有JavaScript错误
window.addEventListener('error', (event) => {
  // 检查是否是已知的markdown相关错误
  const isKnownMarkdownError = event.error && (
    event.error.message?.includes('this.getData is not a function') ||
    event.error.message?.includes('exitCodeText') ||
    event.error.message?.includes('Markdown') ||
    event.error.message?.includes('react-markdown') ||
    event.error.message?.includes('fromMarkdown') ||
    event.error.message?.includes('compile') ||
    event.error.stack?.includes('bundle.js') ||
    event.error.stack?.includes('index.js')
  );

  if (isKnownMarkdownError) {
    // 阻止默认错误行为，不显示错误弹窗
    event.preventDefault();
    event.stopPropagation();

    // 尝试阻止错误冒泡到React错误覆盖
    event.stopImmediatePropagation();

    // 仅在开发模式下记录
    if (process.env.NODE_ENV === 'development') {
      console.log('🛡️ 全局错误拦截器捕获到已知markdown错误，已静默处理');
    }

    return false;
  }

  // 对于未知错误，正常记录
  console.error('全局错误拦截器捕获到未知错误:', event.error);
});

// 额外的错误拦截 - 针对React错误覆盖
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args[0];
  if (typeof message === 'string') {
    // 拦截并静默处理所有markdown相关错误
    if (message.includes('this.getData is not a function') ||
        message.includes('exitCodeText') ||
        message.includes('Uncaught runtime errors') ||
        message.includes('The above error occurred in the <Markdown> component')) {
      // 完全静默处理
      return;
    }
  }
  originalConsoleError.apply(console, args);
};

// 拦截window.onerror以增强错误处理
window.onerror = function(message, source, lineno, colno, error) {
  const isKnownMarkdownError = error && (
    error.message?.includes('this.getData is not a function') ||
    error.message?.includes('exitCodeText') ||
    error.message?.includes('Markdown') ||
    error.message?.includes('react-markdown') ||
    error.message?.includes('fromMarkdown') ||
    error.message?.includes('compile') ||
    error.stack?.includes('bundle.js')
  );

  if (isKnownMarkdownError) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🛡️ window.onerror拦截到已知markdown错误，已静默处理');
    }
    return true; // 阻止默认错误处理
  }

  return false; // 让默认错误处理继续
};

// 强化全局Promise错误拦截器
window.addEventListener('unhandledrejection', (event) => {
  const isKnownMarkdownError = event.reason && (
    event.reason.message?.includes('this.getData is not a function') ||
    event.reason.message?.includes('exitCodeText') ||
    event.reason.message?.includes('Markdown') ||
    event.reason.message?.includes('react-markdown') ||
    event.reason.message?.includes('fromMarkdown') ||
    event.reason.message?.includes('compile') ||
    event.reason.stack?.includes('bundle.js') ||
    event.reason.stack?.includes('index.js')
  );

  if (isKnownMarkdownError) {
    // 阻止Promise拒绝弹窗
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (process.env.NODE_ENV === 'development') {
      console.log('🛡️ 全局Promise拦截器捕获到已知markdown错误，已静默处理');
    }

    return false;
  }

  console.error('全局Promise拦截器捕获到未知错误:', event.reason);
});

export const GlobalContext = createContext();

function App() {
  const [globalConfig, setGlobalConfig] = useState(null);

  useEffect(() => {
    const fetchGlobalConfig = async () => {
      try {
        // 从API获取真实数据
        const url = `${API_URL}/api/global?populate[Banner][populate]=*&populate[Header][populate][logo][populate]=*&populate[Header][populate][navitems][populate]=*&populate[Header][populate][cta][populate]=*&populate[Footer][populate][logo][populate]=*&populate[Footer][populate][navitems][populate]=*&populate[Footer][populate][sociallinks][populate]=*&populate[Footer][populate][copyright][populate]=*&populate[defaultSeo][populate]=*`;
        const response = await fetch(url);
        
        
        if (!response.ok) {
          throw new Error(`HTTP错误! 状态: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data && data.data) {
          // 使用API返回的真实数据
          setGlobalConfig(data.data);
        } else {
          console.error('无效的API全局配置数据结构:', data);
        }
      } catch (error) {
        console.error('获取全局配置时出错:', error);
      }
    };

    fetchGlobalConfig();
  }, []);

  useEffect(() => {
    // 清理函数
    return () => {
      sseManager.disconnect();
    };
  }, []);

  return (
    <AuthProvider>
      <GlobalErrorBoundary>
        <Router>
          <GlobalContext.Provider value={globalConfig}>
            <div className="App">
              <Navbar />
              <main className="main">
                <Suspense fallback={<PageLoading />}>
                  <Routes>
                    {/* 公共路由 */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/articles" element={<ArticleList />} />
                    <Route path="/article/:slug" element={<ArticleDetail />} />
                    <Route path="/weekly" element={<WeeklyList />} />
                    <Route path="/weekly/:slug" element={<WeeklyDetail />} />
                    <Route path="/ideas/:documentId?" element={<IdeasPage />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/stash" element={<StashTypeList />} />
                    <Route path="/stash/:slug" element={<StashList />} />
                    <Route path="/stash/:typeSlug/:stashSlug" element={<StashDetail />} />
                    <Route path="/about" element={<AboutPage />} />

                    {/* 受保护路由 */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/create-idea" element={<CreateIdea />} />
                      <Route path="/create-article" element={<ArticleEditor />} />
                      <Route path="/edit-article/:slug" element={<ArticleEditor />} />
                      <Route path="/profile" element={<ProfilePage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </main>
              <Footer />

            </div>
          </GlobalContext.Provider>
        </Router>
      </GlobalErrorBoundary>
    </AuthProvider>
  );
}

export default App;
